import { Camera, CameraSource, type GalleryImageOptions } from "@capacitor/camera";
import { isDefined } from "@clipboard-health/util-ts";

import { APP_V2_APP_EVENTS, logError } from "../analytics";
import {
  type AcceptableUploadImageContentTypes,
  base64ToBlob,
  getFileTypeFromBlob,
  isAcceptableImageUploadContentType,
} from "../files";
import { CameraPermissionDeniedError } from "./CameraPermissionDeniedError";
import { checkAndRequestPhotoPermissions } from "./checkAndRequestPhotoPermissions";
import { DEFAULT_CAMERA_IMAGE_QUALITY } from "./constants";
import { type UploadFileData } from "./types";

interface PickImagesProps {
  onSuccess: (photos: UploadFileData[]) => void;
  onError: (error: Error | CameraPermissionDeniedError) => void;
  photoOptions?: Pick<GalleryImageOptions, "height" | "width" | "quality">;
}

async function webPathToBase64(webPath: string): Promise<string> {
  const response = await fetch(webPath);
  const blob = await response.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener("loadend", () => {
      const result = reader.result as string;
      // Remove the data URL prefix to get just the base64 part
      const base64 = result.split(",")[1];
      resolve(`data:${blob.type};base64,${base64}`);
    });

    reader.addEventListener("error", reject);
    reader.readAsDataURL(blob);
  });
}

export function usePickImages(props: PickImagesProps): () => Promise<void> {
  const { onSuccess, onError, photoOptions } = props;

  return async () => {
    try {
      // Photos source is used for gallery access
      await checkAndRequestPhotoPermissions(CameraSource.Photos);

      const { photos } = await Camera.pickImages({
        ...photoOptions,
        quality: photoOptions?.quality ?? DEFAULT_CAMERA_IMAGE_QUALITY,
      });

      if (!photos || photos.length === 0) {
        throw new Error("No images selected");
      }

      // Process all photos in parallel to avoid await in loop
      const photoProcessPromises = photos.map(async (photo) => {
        if (!isDefined(photo.webPath)) {
          throw new Error("Invalid photo result - missing webPath");
        }

        const dataUrlBase64 = await webPathToBase64(photo.webPath);
        const blob = await base64ToBlob(dataUrlBase64);
        const { extension, contentType } = await getFileTypeFromBlob(blob);

        if (!isAcceptableImageUploadContentType(contentType)) {
          throw new Error(`Image with unsupported content type picked: ${contentType}`);
        }

        return {
          dataUrlBase64,
          blob,
          extension,
          contentType: contentType as AcceptableUploadImageContentTypes,
        };
      });

      const processedPhotos = await Promise.all(photoProcessPromises);
      onSuccess(processedPhotos);
    } catch (error) {
      const errorLogEvent =
        error instanceof CameraPermissionDeniedError
          ? APP_V2_APP_EVENTS.DOCUMENT_UPLOAD_CAMERA_TAKE_PHOTO_PERMISSION_DENIED
          : APP_V2_APP_EVENTS.DOCUMENT_UPLOAD_MOBILE_FILE_PICKER_FAILURE;

      logError(errorLogEvent, { error });
      onError(error as Error | CameraPermissionDeniedError);
    }
  };
}
